import { PlayCircleOutlined, ReloadOutlined } from "@ant-design/icons";
import { useQuery, useSubscription } from "@apollo/client";
import { Button, Card, Collapse, Form, Select, Spin, Table } from "antd";
import { parseISO } from "date-fns";
import { useEffect, useState } from "react";

import DatePicker from "@src/components/DatePicker";
import SensorGraphs from "@src/components/SensorGraphs/SensorGraphs";
import { getFragmentData } from "@src/graphql";
import { formatAssetErrorId } from "@src/utils/assetErrorHelpers";

import ActionsMenu from "../../components/ActionsMenu/ActionsMenu";
import AssetErrorPopupList from "../../components/AssetErrorPopups/AssetErrorPopupList";
import EdgeStateStatus from "../../components/EdgeStateStatus/EdgeStateStatus";
import LiveStreams from "../../components/LiveStreamPlayer/LiveStreams";
import NavigateButton from "../../components/NavigateButton";
import {
  AssetSignalEventsDocument,
  AssetDetailsFragmentDoc,
  AssetDetailsFragment,
  AssetDetailsDocument,
  OrderDirection,
} from "../../graphql/graphql";
import { formatDate, formatDateOnly } from "../../utils/dateHelpers";

interface AssetDetailsProps {
  factoryId: string;
  assetId: string;
}

interface AssetSignalRecord {
  key: string;
  signal: string;
  timestamp: string;
  assetErrorId: string | undefined;
}

interface FilterValues {
  fromDate: Date | null;
  toDate: Date | null;
  limit?: number;
  orderDirectionByTimestamp?: OrderDirection;
}

const columns = [
  {
    title: "Timestamp",
    dataIndex: "timestamp",
    key: "timestamp",
  },
  {
    title: "Signal",
    dataIndex: "signal",
    key: "signal",
  },
  {
    title: "Error ID",
    key: "error",
    render: (_: string, record: AssetSignalRecord) =>
      record.assetErrorId ? formatAssetErrorId(record.assetErrorId) : "-",
  },
  {
    title: "Actions",
    key: "actions",
    render: (_: string, record: AssetSignalRecord) =>
      record.assetErrorId !== undefined ? (
        <NavigateButton
          name="Show Error"
          targetPath={`asset-signal/${record.key}/asset-error`}
          id={record.assetErrorId}
        />
      ) : (
        <></>
      ),
  },
];

const AssetDetails = ({ factoryId, assetId }: AssetDetailsProps) => {
  const [fromDate, setFromDate] = useState<string | null>(null);
  const [toDate, setToDate] = useState<string | null>(null);
  const [limit, setLimit] = useState<number>(50);
  const [orderDirectionByTimestamp, setOrderDirectionByTimestamp] =
    useState<OrderDirection>(OrderDirection.Desc);

  // Main query for asset details
  const { data, loading } = useQuery(AssetDetailsDocument, {
    variables: {
      factoryId,
      assetId,
    },
  });

  // Separate query for asset signals filters since we don't want to load the entire tree of data
  const {
    data: signalsData,
    loading: signalsLoading,
    refetch: refetchSignals,
  } = useQuery(AssetDetailsDocument, {
    variables: {
      factoryId,
      assetId,
      fromDate,
      toDate,
      limit,
      orderDirectionByTimestamp,
    },
  });

  const subscription = useSubscription(AssetSignalEventsDocument, {
    variables: { assetId: assetId },
  });

  const [asset, setAsset] = useState<AssetDetailsFragment | null>(null);
  const [assetSignals, setAssetSignals] = useState<AssetSignalRecord[]>([]);
  const [playAllButtonClicked, setPlayAllButtonClicked] = useState(false);

  const handlePlayAllButtonClick = () => {
    setPlayAllButtonClicked(true);
  };

  useEffect(() => {
    if (!loading && data?.factory) {
      if (
        data.factory.__typename === "QueryFactorySuccess" &&
        data.factory.data.asset.__typename === "FactoryAssetSuccess"
      ) {
        const assetDetails = getFragmentData(
          AssetDetailsFragmentDoc,
          data.factory.data.asset.data,
        );

        setAsset(assetDetails);
      }
    }
  }, [data, loading]);

  useEffect(() => {
    if (!signalsLoading && signalsData?.factory) {
      if (
        signalsData.factory.__typename === "QueryFactorySuccess" &&
        signalsData.factory.data.asset.__typename === "FactoryAssetSuccess"
      ) {
        const assetDetails = getFragmentData(
          AssetDetailsFragmentDoc,
          signalsData.factory.data.asset.data,
        );

        setAssetSignals(
          assetDetails.assetSignals.map((signal) => ({
            key: signal.id,
            signal: signal.signal,
            timestamp: formatDate(signal.timestamp),
            assetErrorId: signal.assetError?.id,
          })),
        );
      }
    }
  }, [signalsData, signalsLoading]);

  useEffect(() => {
    if (subscription.data) {
      const newSignal = {
        key: subscription.data.assetSignalEvents.assetSignal.id,
        signal: subscription.data.assetSignalEvents.assetSignal.signal,
        timestamp: formatDate(
          subscription.data.assetSignalEvents.assetSignal.timestamp,
        ),
        assetErrorId:
          subscription.data.assetSignalEvents.assetSignal.assetError?.id ??
          undefined,
      };
      setAssetSignals((prevAssetSignals) => {
        const updatedSignals = [newSignal, ...prevAssetSignals];
        return updatedSignals.slice(0, limit);
      });
    }
  }, [subscription.data, limit]);

  const handleFilterSubmit = (values: FilterValues) => {
    const { fromDate, toDate, limit, orderDirectionByTimestamp } = values;

    const formattedFromDate = formatDateOnly(fromDate);
    const formattedToDate = formatDateOnly(toDate);

    setFromDate(formattedFromDate);
    setToDate(formattedToDate);
    setLimit(limit ?? 50);
    setOrderDirectionByTimestamp(
      orderDirectionByTimestamp ?? OrderDirection.Desc,
    );

    void refetchSignals({
      factoryId,
      assetId,
      fromDate: formattedFromDate,
      toDate: formattedToDate,
      limit: limit ?? 50,
      orderDirectionByTimestamp:
        orderDirectionByTimestamp ?? OrderDirection.Desc,
    });
  };

  const resetFilters = () => {
    setFromDate(null);
    setToDate(null);
    setLimit(50);
    setOrderDirectionByTimestamp(OrderDirection.Desc);

    void refetchSignals({
      factoryId,
      assetId,
      fromDate: null,
      toDate: null,
      limit: 50,
      orderDirectionByTimestamp: OrderDirection.Desc,
    });
  };

  const AssetSignalFilters = () => {
    const [form] = Form.useForm<FilterValues>();

    useEffect(() => {
      form.setFieldsValue({
        fromDate: fromDate ? parseISO(fromDate) : null,
        toDate: toDate ? parseISO(toDate) : null,
        limit: limit,
        orderDirectionByTimestamp: orderDirectionByTimestamp,
      });
    }, [form]);

    const handleLimitChange = (value: number) => {
      form.setFieldsValue({ limit: value });
      const values = form.getFieldsValue();
      handleFilterSubmit(values);
    };

    const handleOrderByChange = (value: OrderDirection) => {
      form.setFieldsValue({ orderDirectionByTimestamp: value });
      const values = form.getFieldsValue();
      handleFilterSubmit(values);
    };

    const handleDateChange = (field: string, date: Date | null) => {
      form.setFieldsValue({ [field]: date });
      const values = form.getFieldsValue();

      // Only apply filter if both dates are set or both are null/undefined
      if (
        (values.fromDate && values.toDate) ||
        (!values.fromDate && !values.toDate)
      ) {
        handleFilterSubmit(values);
      }
    };

    return (
      <Form
        form={form}
        layout="inline"
        style={{ marginBottom: 16 }}
        initialValues={{
          limit: limit,
          orderDirectionByTimestamp: orderDirectionByTimestamp,
        }}
      >
        <Form.Item name="fromDate" label="From Date">
          <DatePicker
            onChange={(date) => {
              handleDateChange("fromDate", date);
            }}
          />
        </Form.Item>
        <Form.Item name="toDate" label="To Date">
          <DatePicker
            onChange={(date) => {
              handleDateChange("toDate", date);
            }}
          />
        </Form.Item>
        <Form.Item name="limit" label="Limit">
          <Select
            style={{ width: 80 }}
            onChange={handleLimitChange}
            value={limit}
          >
            <Select.Option value={25}>25</Select.Option>
            <Select.Option value={50}>50</Select.Option>
            <Select.Option value={100}>100</Select.Option>
            <Select.Option value={250}>250</Select.Option>
          </Select>
        </Form.Item>
        <Form.Item name="orderDirectionByTimestamp" label="Order">
          <Select
            style={{ width: 100 }}
            onChange={handleOrderByChange}
            value={orderDirectionByTimestamp}
          >
            <Select.Option value={OrderDirection.Desc}>Newest</Select.Option>
            <Select.Option value={OrderDirection.Asc}>Oldest</Select.Option>
          </Select>
        </Form.Item>
        <Form.Item style={{ marginLeft: "auto" }}>
          <Button onClick={resetFilters} type="primary">
            <ReloadOutlined />
            Reset Filters
          </Button>
        </Form.Item>
      </Form>
    );
  };

  const renderAssetSignalsTable = (assetSignals: AssetSignalRecord[]) => (
    <div style={{ overflowX: "auto" }}>
      <AssetSignalFilters />
      <Table
        bordered={false}
        columns={columns}
        dataSource={assetSignals}
        pagination={{ pageSize: 6, showSizeChanger: false }}
        scroll={{ x: true }}
        style={{ width: "100%" }}
        loading={signalsLoading}
      />
    </div>
  );

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Spin />
      </div>
    );
  }

  return (
    <>
      <div
        style={{
          position: "fixed",
          top: 50,
          right: 0,
          zIndex: 1000,
        }}
      >
        <AssetErrorPopupList factoryId={factoryId} />
      </div>
      <ActionsMenu
        assetId={assetId}
        showAssetCommands={!!asset?.configuration.showAssetCommands}
        hasUnhandledErrors={!!asset?.hasUnhandledErrors}
        hasUnresolvedErrors={!!asset?.hasUnresolvedErrors}
      />
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          gap: "10px",
        }}
      >
        <Card
          bordered={false}
          style={{ marginTop: "20px" }}
          title={
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                {assetId}
                <EdgeStateStatus
                  assetId={assetId}
                  lastOnlineAt={asset?.lastOnlineAt}
                  latestEdgeServiceVersion={asset?.latestEdgeServiceVersion}
                />
              </div>
              <div
                style={{
                  marginLeft: "auto",
                }}
              >
                <Button type="primary" onClick={handlePlayAllButtonClick}>
                  <PlayCircleOutlined /> Play all
                </Button>
              </div>
            </div>
          }
        >
          <LiveStreams
            factoryId={factoryId}
            assetId={assetId}
            playAllButtonClicked={playAllButtonClicked}
          />
        </Card>
        <Card bordered={false}>
          {asset?.configuration.showSensorData ? (
            <Collapse
              items={[
                {
                  key: "1",
                  label: "Asset Signals",
                  children: renderAssetSignalsTable(assetSignals),
                },
                {
                  key: "2",
                  label: "Sensor Data",
                  children: <SensorGraphs assetId={assetId} />,
                },
              ]}
              defaultActiveKey={["1"]}
            />
          ) : (
            renderAssetSignalsTable(assetSignals)
          )}
        </Card>
      </div>
    </>
  );
};

export default AssetDetails;
